import { useAsyncData, useNuxtApp, useRoute } from '#imports'
import { type ComputedRef, computed } from 'vue'

import type { ModelComparisonCompareProduct } from '@backmarket/http-api/src/api-specs-model-comparison/models/compare-models'
import {
  cleanTableData,
  getScoreField,
  getSpecificationField,
  getSpecificationFields,
} from '@backmarket/nuxt-layer-recommendation/comparisonHelper.ts'
import type { Scores } from '@backmarket/nuxt-layer-recommendation/models/product'
import type { TableData } from '@backmarket/nuxt-layer-recommendation/models/productComparison'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import {
  PRODUCT_COMPARISON_CAMERA_FIELDS,
  PRODUCT_COMPARISON_DURABILITY_FIELDS,
  PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
  PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
  PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
  PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
  PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
} from './useFetchModelComparisonV2.constants'
import translations from './useFetchModelComparisonV2.translations'

type ProductSpecification = {
  field: string
  display: string
  values: Array<{
    label: string
  }>
}
type ReviewRating = {
  averageRate: number
  count: number
}

type Product = {
  product: ModelComparisonCompareProduct
  review: {
    averageRate: number
    count: number
  }
  specifications: ProductSpecification[]
}

export function useFetchModelsComparisonV2() {
  const $httpFetch = useHttpFetch()
  const nuxtApp = useNuxtApp()
  const i18n = useI18n()
  const route = useRoute()

  const {
    data: products,
    error,
    pending,
  } = useAsyncData<Product[]>(
    getAsyncDataKey('models-comparison-v2', {
      slug1: route.params.productA,
      slug2: route.params.productB,
    }),
    async () => {
      const {
        products: compareProducts,
      }: { products: ModelComparisonCompareProduct[] } = await $httpFetch(
        '/product-page/compare/:model1/:model2',
        {
          pathParams: {
            model1: route.params.productA as string,
            model2: route.params.productB as string,
          },
        },
      )
      const productIds = compareProducts.map((p) => p.productId)
      const [reviews, technicalSpecifications] = await Promise.all([
        Promise.all(
          productIds.map((productId: string) =>
            nuxtApp.runWithContext(
              () =>
                $httpFetch('/reviews/v1/products/:productId/reviews-summary', {
                  pathParams: { productId },
                }) as Promise<ReviewRating>,
            ),
          ),
        ),
        Promise.all(
          productIds.map((productId: string) =>
            nuxtApp.runWithContext(() =>
              $httpFetch('/bm/product/:productId/technical_specifications', {
                pathParams: { productId },
              }),
            ),
          ),
        ),
      ])

      return compareProducts.map((product, index: number): Product => {
        let specifications = []

        try {
          specifications = JSON.parse(
            JSON.stringify(technicalSpecifications[index].specifications || []),
          )
        } catch (err) {
          specifications = []
        }

        return {
          product,
          review: reviews[index],
          specifications,
        }
      })
    },
  )

  const productsSpecifications = computed(() =>
    products.value?.map((p) => p.specifications),
  )

  const productsScores: ComputedRef<Scores[]> = computed(
    () =>
      products.value?.map((p) => {
        return Object.fromEntries(p.product.scores.map((s) => [s.id, s.value]))
      }) || [],
  )

  const tableData = computed<TableData[]>(() => {
    if (!products.value || !productsSpecifications.value) return []
    const res: TableData[] = [
      {
        sections: [
          {
            lines: [
              {
                type: 'ratings',
                title: i18n(translations.ratings),
                values:
                  products.value?.map((p) => p.review?.averageRate || 0) ?? [],
              },
            ],
          },
        ],
      },
      {
        title: i18n(translations.scores),
        sections: [
          {
            title: i18n(translations.durability),
            trackingKey: 'comparison_tool_durability',
            lines: [
              getScoreField(productsScores.value, 'life_expectancy_score'),
              ...PRODUCT_COMPARISON_DURABILITY_FIELDS.map((field) =>
                getSpecificationField(
                  productsSpecifications.value ?? [],
                  field,
                ),
              ),
            ],
          },
          {
            title: i18n(translations.performance),
            trackingKey: 'comparison_tool_performance',
            lines: [
              getScoreField(productsScores.value, 'performances_score'),
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.screenQuality),
            trackingKey: 'comparison_tool_screen_quality',
            lines: [
              getScoreField(productsScores.value, 'screen_quality_score'),
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.soundQuality),
            trackingKey: 'comparison_tool_sound_quality',
            lines: [
              getScoreField(productsScores.value, 'sound_quality_scrore'),
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.multimedia),
            trackingKey: 'comparison_tool_multimedia',
            lines: [
              getScoreField(productsScores.value, 'multimedia_score'),
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
              ),
            ],
          },
          {
            title: i18n(translations.camera),
            trackingKey: 'comparison_tool_camera',
            lines: [
              getScoreField(productsScores.value, 'camera_score'),
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_CAMERA_FIELDS,
              ),
            ],
          },
        ],
      },
      {
        title: i18n(translations.miscelaneous),
        separator: true,
        sections: [
          {
            trackingKey: 'comparison_tool_miscelaneous',
            lines: [
              ...getSpecificationFields(
                productsSpecifications.value,
                PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
              ),
            ],
          },
        ],
      },
    ]

    return cleanTableData(res)
  })

  const title = computed(() =>
    i18n(translations.resultsTitle, {
      firstModel: products.value?.[0].product.title,
      secondModel: products.value?.[1].product.title,
    }),
  )

  return {
    products,
    error,
    tableData,
    title,
    pending,
  }
}
